// LA FRISE DES ÉPOQUES — le miroir d'affichage des couches d'Autun.
//
// Le vocabulaire vient du CANON (backend/content_loader.FRISE_COUCHES — un
// bloc `frise:` fermé et obligatoire dans chaque fiche, validé au démarrage) ;
// ce module n'en est que le miroir : l'ordre ci-dessous est l'ordre du rail,
// et chaque clef se traduit par i18n — jamais le jeton canon servi tel quel.
//
// AUCUN nombre en dur. Les comptes descendent de l'API (`couches[].total`,
// calculé sur le canon) : le jour où une fiche naît, la bande le dit toute
// seule. C'est la leçon des « huit thèmes » — un nombre écrit dans un écran
// finit toujours par mentir.
//
// Deux doutes cohabitent et ne doivent JAMAIS fusionner :
//   · `certitude: 'debattue'`  — le canon doute de la DATATION ;
//   · `attribution_debattue`   — le canon doute de l'AUTEUR.
// Une fiche peut porter les deux, et c'est juste : on ne sait ni quand ni de
// qui. Un seul badge pour les deux dirait à l'invité qu'on ne sait rien,
// alors qu'on sait précisément quoi.
//
// Le second descend depuis le 25/08/2026 d'un champ DÉCLARÉ du canon
// (`attribution`), plus d'une expression régulière sur la prose de
// `confidence` — voir `lib/attribution.js` pour le défaut que cela corrige.

import { isAttributionDebattue } from './attribution';

/** Ordre d'affichage des couches — miroir de l'énumération backend. */
export const FRISE_COUCHES = [
    'avant-rome',
    'augustodunum',
    'ville-tardive',
    'abbayes',
    'gislebertus',
    'rolin',
    'classique',
    'erudits',
    'encore',
    'hors-du-temps',
];

/** Le tiroir NOMMÉ des fiches sans date lisible — jamais un oubli. */
export const FRISE_HORS_DU_TEMPS = 'hors-du-temps';

/**
 * LES COUCHES DATÉES — l'axe chronologique, sans le tiroir.
 *
 * « Hors du temps » n'est pas une époque : c'est l'aveu qu'une fiche ne se
 * date pas. Il occupe la dernière case du rail QUAND il porte quelque chose,
 * et il disparaît sinon (`railCouches` écarte les couches vides). Un écran qui
 * doit RÉSERVER la place de l'axe avant de connaître la charge — le squelette
 * des voies parallèles — réserve donc neuf bandes, pas dix : dessiner le
 * tiroir reviendrait à parier qu'une fiche du canon avoue ne pas se dater, ce
 * que le squelette ne peut pas savoir (Loi 1). Au 23/08/2026 il est vide : ses
 * trois fiches portaient des horaires d'ouverture en guise de datation.
 */
export const FRISE_COUCHES_DATEES = FRISE_COUCHES.filter(
    (key) => key !== FRISE_HORS_DU_TEMPS,
);

const NAME_KEYS = {
    'avant-rome': 'friseCoucheAvantRome',
    augustodunum: 'friseCoucheAugustodunum',
    'ville-tardive': 'friseCoucheVilleTardive',
    abbayes: 'friseCoucheAbbayes',
    gislebertus: 'friseCoucheGislebertus',
    rolin: 'friseCoucheRolin',
    classique: 'friseCoucheClassique',
    erudits: 'friseCoucheErudits',
    encore: 'friseCoucheEncore',
    'hors-du-temps': 'friseCoucheHorsDuTemps',
};

const BORNES_KEYS = {
    'avant-rome': 'friseBornesAvantRome',
    augustodunum: 'friseBornesAugustodunum',
    'ville-tardive': 'friseBornesVilleTardive',
    abbayes: 'friseBornesAbbayes',
    gislebertus: 'friseBornesGislebertus',
    rolin: 'friseBornesRolin',
    classique: 'friseBornesClassique',
    erudits: 'friseBornesErudits',
    encore: 'friseBornesEncore',
    'hors-du-temps': 'friseBornesHorsDuTemps',
};

const FAMILLE_KEYS = {
    monument: 'friseFamilleMonument',
    tresor: 'friseFamilleTresor',
    personnage: 'friseFamillePersonnage',
    evenement: 'friseFamilleEvenement',
};

/** Ordre de rendu des familles À L'INTÉRIEUR d'une couche : les monuments
 * d'abord (on marche dedans), puis les trésors, les figures, les rendez-vous.
 * Elles restent MÊLÉES dans une seule grille — la famille se lit d'un coup
 * d'œil au repère gravé, elle ne sépare pas la couche en rayons. */
export const FRISE_FAMILLES = ['monument', 'tresor', 'personnage', 'evenement'];

/** i18n key du nom d'une couche ; null pour une valeur inconnue (un paquet
 * d'API périmé) — l'appelant ne rend alors RIEN, jamais le jeton brut. */
export function coucheNameKey(key) {
    return NAME_KEYS[key] || null;
}

/** i18n key de la ligne de bornes (« XIVᵉ – XVᵉ siècle »). */
export function coucheBornesKey(key) {
    return BORNES_KEYS[key] || null;
}

/** i18n key du nom de famille d'une fiche (Monument · Trésor · Figure…). */
export function familleLabelKey(famille) {
    return FAMILLE_KEYS[famille] || null;
}

/**
 * Les couches à montrer sur la bande, dans l'ordre du canon, VIDES EXCLUES.
 *
 * Une pastille sans fiche serait une porte qui ne mène nulle part — et le
 * jour où le canon date enfin les Journées romaines, « Hors du temps »
 * disparaît tout seul, sans qu'on ait à y penser.
 *
 * @param {Array<{key: string, total: number}>} couches — la charge de l'API
 */
export function railCouches(couches) {
    const byKey = new Map(
        (Array.isArray(couches) ? couches : [])
            .filter((c) => c && typeof c.key === 'string')
            .map((c) => [c.key, c]),
    );
    return FRISE_COUCHES
        .map((key) => byKey.get(key))
        .filter((c) => c && Number(c.total) > 0);
}

/** Les fiches d'une couche, familles MÊLÉES, dans l'ordre de rendu. */
export function fichesOfCouche(fiches, key) {
    const list = (Array.isArray(fiches) ? fiches : []).filter(
        (f) => f && Array.isArray(f.couches) && f.couches.includes(key),
    );
    return FRISE_FAMILLES.flatMap((famille) =>
        list.filter((f) => f.famille === famille),
    ).concat(list.filter((f) => !FRISE_FAMILLES.includes(f.famille)));
}

/** Les AUTRES couches d'une fiche — ce qui fait de la frise une promenade.
 * Rend les clefs du vocabulaire uniquement, dans l'ordre chronologique. */
export function autresCouches(fiche, currentKey = null) {
    const own = (fiche && Array.isArray(fiche.couches) ? fiche.couches : []);
    return FRISE_COUCHES.filter(
        (key) => key !== currentKey && own.includes(key),
    );
}

/** La fiche de la frise portant cet id, ou null. Le renvoi « d'autres pièces
 * de ce siècle » s'en sert pour retrouver les couches d'un trésor ouvert. */
export function ficheById(fiches, id) {
    if (!id) return null;
    return (Array.isArray(fiches) ? fiches : []).find((f) => f && f.id === id) || null;
}

/** La datation est-elle débattue ? (à ne pas confondre avec l'attribution) */
export const isDatationDebattue = (fiche) => fiche?.certitude === 'debattue';

/** Le badge « Datation débattue » doit-il s'afficher À CÔTÉ de la ligne de
 * date ? Non quand cette ligne le dit déjà mot pour mot — quatre fiches du
 * canon sont dans ce cas (Tour des Ursulines, Pectorios et sa figure, les
 * moules du théâtre), et le lecteur y voyait deux réserves là où il n'y en a
 * qu'une (Steven, 07/09/2026).
 *
 * ⚑ Le doute n'est pas retiré du canon, il est dit UNE fois : le serveur
 * répond `doute_dans_la_dateline` en lisant `frise.note_fr`, que le chargeur
 * oblige à se retrouver VERBATIM dans la prose. Et le COMPTE des doutes, sur
 * la bande des voies, continue de lire `isDatationDebattue` — on affiche
 * moins de badges, on ne compte pas moins de doutes. */
export const showsDatationBadge = (fiche) =>
    isDatationDebattue(fiche) && !fiche?.doute_dans_la_dateline;

/** L'attribution est-elle débattue ? Le canon doute de QUI, pas de QUAND —
 * l'autre doute, et il ne se confond jamais avec le précédent.
 *
 * La règle vit dans `lib/attribution.js`, avec celle des fiches personnages et
 * trésors : une seule rédaction, parce que deux divergent (25/08/2026). Ce
 * re-export garde l'import historique de la frise valide — et le module s'en
 * sert lui-même plus bas (`voiesAxis`), donc il l'importe pour de vrai. */
export { isAttributionDebattue };

// ── LES VOIES PARALLÈLES — la SECONDE lecture de la frise ────────────────────
//
// La bande lit UNE époque à la fois. Cet axe-ci les met toutes côte à côte,
// les quatre familles croisant les dix couches, pour qu'un coup d'œil montre
// ce qu'aucune fiche ne dit : Augustodunum est une ville qu'on a BÂTIE, le
// siècle des Rolin une ville qu'on a ORNÉE, « ceux qui creusent encore » une
// époque de GENS. Rien de neuf n'est affirmé — ce sont les comptes que l'API
// calcule déjà sur le canon, simplement rangés.
//
// L'AXE N'EST PAS LINÉAIRE, ET C'EST LA DÉCISION QUI TIENT TOUT. 67 fiches
// tiennent en 315 ans (Augustodunum) et 10 en 500 (les abbayes) : un axe en
// années écraserait la première en un trait et étirerait la seconde en désert,
// donc la lecture verticale — comparer une voie à travers le temps — ne
// voudrait plus rien dire. L'unité de l'axe est donc LA COUCHE, chacune de
// hauteur égale, comme le font les frises synchronoptiques face à une densité
// inégale. Le temps y est ordinal, jamais métrique : la vue ne prétend nulle
// part qu'une bande dure autant qu'une autre — elle porte ses bornes écrites.

/** Le compte d'UNE voie dans UNE couche — 0 pour tout ce qui n'est pas un
 * nombre positif servi par l'API. Zéro est une INFORMATION (« aucun
 * rendez-vous daté avant le XXᵉ siècle »), jamais une case à masquer. */
export function voieCount(couche, famille) {
    const n = Number(couche?.familles?.[famille]);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * L'axe des voies parallèles : une bande par couche, dans l'ordre du canon,
 * vides exclues (même règle que `railCouches` — jamais une porte qui ne mène
 * nulle part), et dans chaque bande les quatre voies dans l'ordre de rendu.
 *
 * Le total d'une bande est la SOMME DES VOIES AFFICHÉES, pas `couche.total` :
 * un total qui ne serait pas celui des quatre nombres sous les yeux de
 * l'invité serait un cinquième nombre à croire sur parole.
 *
 * Les deux doutes du canon montent avec : ils se comptent sur les fiches de la
 * couche, séparément, et ils ne fusionnent jamais.
 *
 * @param {Array} couches — `couches[]` de l'API
 * @param {Array} fiches  — `fiches[]` de l'API (pour les deux doutes)
 */
export function voiesAxis(couches, fiches) {
    const all = Array.isArray(fiches) ? fiches : [];
    return railCouches(couches).map((couche) => {
        const lanes = FRISE_FAMILLES.map((famille) => ({
            famille,
            total: voieCount(couche, famille),
        }));
        const membres = all.filter(
            (f) => f && Array.isArray(f.couches) && f.couches.includes(couche.key),
        );
        return {
            key: couche.key,
            lanes,
            total: lanes.reduce((sum, lane) => sum + lane.total, 0),
            datationDebattue: membres.filter(isDatationDebattue).length,
            attributionDebattue: membres.filter(isAttributionDebattue).length,
        };
    });
}

/** L'échelle COMMUNE des barres : la voie la plus fournie de tout l'axe la
 * remplit. Une échelle par bande rendrait chaque bande jolie et la comparaison
 * verticale fausse — c'est justement cette comparaison qui est le produit.
 * Jamais un maximum écrit : il se mesure sur ce que l'écran montre. */
export function voiesEchelle(axis) {
    const totals = (Array.isArray(axis) ? axis : []).flatMap(
        (row) => (row?.lanes || []).map((lane) => Number(lane.total) || 0),
    );
    return Math.max(1, ...totals);
}
